import { ApiError, RequestTimeout } from './errors.js';
import { LEGAL_NOTICE } from './legal-notice.js';

/**
 * What went wrong, in words somebody can act on.
 *
 * Every screen here used to turn every failure into one sentence of its own —
 * "La liste des appels n'a pas pu être chargée", "Cet appel est introuvable,
 * ou vous n'y avez pas accès" — and the sentence was chosen before the failure
 * was known. So an expired session, a rate limit, a database that refused a
 * query and a phone with no signal all read the same, and all read as the
 * salon's fault.
 *
 * The cost is not tidiness. It is that **the screen destroys the evidence**:
 * `ApiError` arrives carrying the server's stable code and the request id the
 * server echoed for exactly this purpose, and both were dropped on the floor.
 * A customer who says "je n'ai pas accès au détail de l'appel" has then told us
 * everything they can see, and it is not enough to tell a 404 from a 500 — so
 * the next step is a guess, which is how an afternoon goes.
 *
 * This branches on `code`, never on `message`: a message is for a person to
 * read, and matching on one is how a copy-editing change breaks error
 * handling. `notFound` is a parameter because 404 is the one code whose
 * meaning is genuinely local — an absent call and an establishment with no
 * fiche need different sentences and different next steps. A screen that has
 * nothing true to say about a 404 passes nothing, and it falls through to the
 * generic text with the reference number, which invents nothing.
 */
export function describeFailure(
  cause: unknown,
  options: { readonly notFound?: string } = {},
): string {
  if (cause instanceof RequestTimeout) {
    // A write that went unanswered may still have happened. Saying "try
    // again" there is how a salon ends up with two numbers, or two answers to
    // the same question.
    return cause.write
      ? 'Le serveur n’a pas répondu à temps. Votre demande a peut-être été prise en compte : rechargez la page pour le vérifier avant de recommencer.'
      : 'Le serveur met trop de temps à répondre. Réessayez dans un instant.';
  }

  if (!(cause instanceof ApiError)) {
    // Never reached the server, or never came back: a flaky connection, a
    // reload mid-request. Not something a support ticket can help with.
    return 'Le serveur n’a pas répondu. Vérifiez votre connexion et réessayez.';
  }

  switch (cause.code) {
    case 'authn.required':
      return 'Votre session a expiré. Reconnectez-vous.';
    case 'authz.denied':
      return 'Votre rôle ne permet pas de consulter cette information. Demandez-le au propriétaire du compte.';
    case 'rate_limited':
      return 'Trop de requêtes en peu de temps. Patientez un instant avant de réessayer.';
    case 'validation.failed':
      return 'Cette demande a été refusée : un paramètre n’est pas valide.';
    case 'resource.not_found':
      return options.notFound ?? generic(cause);
    default:
      return generic(cause);
  }
}

/**
 * Whether asking again could change the answer.
 *
 * « Réessayer » is offered only when it could. A refusal for a role, a call
 * that does not exist, a session that has expired: each comes back identical
 * however many times it is asked, and a button that changes nothing teaches
 * somebody that the buttons here do nothing. What can change is the network,
 * a server having a bad minute, and a rate limit running out.
 */
export function worthRetrying(cause: unknown): boolean {
  if (cause instanceof RequestTimeout) return !cause.write;
  if (!(cause instanceof ApiError)) return true;
  return cause.status >= 500 || cause.code === 'rate_limited';
}

/** A failure as a screen keeps it: what to say, and whether to offer to ask again. */
export interface Failure {
  readonly message: string;
  readonly retry: boolean;
}

/**
 * The failure of a load, ready for `LoadFailure`.
 *
 * `describe` is the screen's own wording when it has one — the codes whose
 * meaning is local to it — and falls through to `describeFailure` for the rest.
 */
export function failureOf(
  cause: unknown,
  describe: (cause: unknown) => string = describeFailure,
): Failure {
  return { message: describe(cause), retry: worthRetrying(cause) };
}

/**
 * The fallback, and the reason the request id is on the screen at all.
 *
 * A 500 is ours, not the salon's, and the only thing that makes it findable in
 * a log is the identifier the server already generated. Printing it costs a
 * line and turns "ça ne marche pas" into a query.
 *
 * Worded for a page that would not load and a change that would not save
 * alike: every screen's own wording falls through to this one, and the title
 * above it already says which of the two happened.
 */
function generic(cause: ApiError): string {
  const ours = cause.status >= 500;
  if (cause.requestId === null) {
    return ours
      ? `Un problème est survenu de notre côté. Réessayez dans un instant, et écrivez à ${LEGAL_NOTICE.contactEmail} si cela persiste.`
      : 'Une erreur est survenue. Réessayez dans un instant.';
  }
  return ours
    ? `Un problème est survenu de notre côté. Nous pouvons le retrouver avec cette référence : ${cause.requestId}.`
    : `Une erreur est survenue (référence ${cause.requestId}).`;
}
